import { Text } from "@mantine/core";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import type {
  SiteOverviewSite,
  SiteStatusLogs,
  SiteStatusSite
} from "../../site-status-types";

enum LogFilterLabel {
  Info = "Infos",
  Warnings = "Warnungen",
  Errors = "Blocker",
  None = "Keine"
}

interface ConsumptionAnalysisColumnProps {
  onClickLogsCell: (logs: SiteStatusLogs) => void;
}

function getConsumptionAnalysisColumn({
  onClickLogsCell
}: ConsumptionAnalysisColumnProps): MRT_ColumnDef<
  SiteOverviewSite | SiteStatusSite
> {
  return {
    header: "Verbrauchsanalyse",
    accessorKey: "logs",
    Cell: ({ cell }) => <LogsCell logs={cell.getValue<SiteStatusLogs>()} />,
    mantineTableBodyCellProps: ({ cell }) => {
      const logs = cell.getValue<SiteStatusLogs>();
      const clickable =
        logs.infos.length > 0 ||
        logs.warnings.length > 0 ||
        logs.errors.length > 0;
      return {
        onClick: () => {
          if (clickable) {
            onClickLogsCell(logs);
          }
        },
        style: clickable ? { cursor: "pointer" } : {}
      };
    },
    enableGlobalFilter: false,
    sortingFn: (rowA, rowB) => {
      const aLogs = rowA.getValue<SiteStatusLogs>("logs");
      const bLogs = rowB.getValue<SiteStatusLogs>("logs");
      return sortLogs(aLogs, bLogs);
    },
    filterVariant: "multi-select",
    mantineFilterMultiSelectProps: {
      data: Object.values(LogFilterLabel)
    },
    filterFn: (row, _columnId, filterValue) => {
      if (filterValue.length === 0) {
        return true;
      }

      const logs = row.getValue<SiteStatusLogs>("logs");
      const hasInfo = logs.infos.length > 0;
      const hasWarnings = logs.warnings.length > 0;
      const hasErrors = logs.errors.length > 0;

      if (filterValue.includes(LogFilterLabel.Info) && hasInfo) {
        return true;
      }
      if (filterValue.includes(LogFilterLabel.Warnings) && hasWarnings) {
        return true;
      }
      if (filterValue.includes(LogFilterLabel.Errors) && hasErrors) {
        return true;
      }
      if (
        filterValue.includes(LogFilterLabel.None) &&
        !hasInfo &&
        !hasWarnings &&
        !hasErrors
      ) {
        return true;
      }

      return false;
    }
  };
}

interface LogsCellProps {
  logs: SiteStatusLogs;
}

function LogsCell({ logs }: LogsCellProps) {
  const numInfo = logs.infos.length;
  const numWarnings = logs.warnings.length;
  const numErrors = logs.errors.length;

  return (
    <div>
      <Text>{numInfo} Infos</Text>
      <Text
        c={numWarnings > 0 ? "warning" : undefined}
        fw={numWarnings > 0 ? "bold" : undefined}
      >
        {numWarnings} Warnungen
      </Text>
      <Text
        c={numErrors > 0 ? "error" : undefined}
        fw={numErrors > 0 ? "bold" : undefined}
      >
        {numErrors} Blocker
      </Text>
    </div>
  );
}

function sortLogs(a: SiteStatusLogs, b: SiteStatusLogs) {
  if (a.errors.length !== b.errors.length) {
    return a.errors.length - b.errors.length;
  }
  if (a.warnings.length !== b.warnings.length) {
    return a.warnings.length - b.warnings.length;
  }
  if (a.infos.length !== b.infos.length) {
    return a.infos.length - b.infos.length;
  }
  return 0;
}

export { getConsumptionAnalysisColumn };
